import React, { useState, useMemo } from 'react';
import { DollarSign, Calendar, Percent, Building2 } from 'lucide-react';
import CalculatorInput from '../shared/CalculatorInput';
import ResultCard from '../shared/ResultCard';
import Chart from '../shared/Chart';

const Four01kCalculator: React.FC = () => {
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(65);
  const [currentBalance, setCurrentBalance] = useState(25000);
  const [annualSalary, setAnnualSalary] = useState(75000);
  const [contributionRate, setContributionRate] = useState(6);
  const [employerMatch, setEmployerMatch] = useState(50);
  const [matchLimit, setMatchLimit] = useState(6);
  const [annualReturn, setAnnualReturn] = useState(7);

  const calculatedData = useMemo(() => {
    const years = retirementAge - currentAge;
    const monthlyRate = annualReturn / 100 / 12;
    const monthlyContribution = (annualSalary * (contributionRate / 100)) / 12;
    const employerContribution = Math.min(
      monthlyContribution * (employerMatch / 100),
      (annualSalary * (matchLimit / 100)) / 12
    );
    const totalMonthlyContribution = monthlyContribution + employerContribution;
    const months = years * 12;
    const data = [];

    let balance = currentBalance;
    let totalContributed = currentBalance;
    let employerTotal = 0;

    for (let i = 0; i <= months; i++) {
      if (i > 0) {
        balance = balance * (1 + monthlyRate) + totalMonthlyContribution;
        totalContributed += monthlyContribution;
        employerTotal += employerContribution;
      }

      if (i % 12 === 0) {
        data.push({
          year: currentAge + i / 12,
          balance: Math.round(balance),
          contributed: Math.round(totalContributed),
          employer: Math.round(employerTotal),
        });
      }
    }

    return data;
  }, [currentAge, retirementAge, currentBalance, annualSalary, contributionRate, employerMatch, matchLimit, annualReturn]);

  const finalBalance = calculatedData[calculatedData.length - 1]?.balance || 0;
  const employerTotal = calculatedData[calculatedData.length - 1]?.employer || 0;
  const yourContributions = calculatedData[calculatedData.length - 1]?.contributed || 0;

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 sm:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">401(k) Calculator</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <CalculatorInput
          label="Current Age"
          value={currentAge}
          onChange={setCurrentAge}
          icon={<Calendar size={20} />}
          suffix="years"
        />
        <CalculatorInput
          label="Retirement Age"
          value={retirementAge}
          onChange={setRetirementAge}
          icon={<Calendar size={20} />}
          suffix="years"
        />
        <CalculatorInput
          label="Current Balance"
          value={currentBalance}
          onChange={setCurrentBalance}
          icon={<DollarSign size={20} />}
          prefix="$"
        />
        <CalculatorInput
          label="Annual Salary"
          value={annualSalary}
          onChange={setAnnualSalary}
          icon={<DollarSign size={20} />}
          prefix="$"
        />
        <CalculatorInput
          label="Contribution Rate"
          value={contributionRate}
          onChange={setContributionRate}
          icon={<Percent size={20} />}
          suffix="%"
        />
        <CalculatorInput
          label="Employer Match"
          value={employerMatch}
          onChange={setEmployerMatch}
          icon={<Building2 size={20} />}
          suffix="%"
        />
        <CalculatorInput
          label="Match Limit"
          value={matchLimit}
          onChange={setMatchLimit}
          icon={<Percent size={20} />}
          suffix="%"
        />
        <CalculatorInput
          label="Expected Return"
          value={annualReturn}
          onChange={setAnnualReturn}
          icon={<Percent size={20} />}
          suffix="%"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <ResultCard
          title="Total Balance"
          value={finalBalance}
          description="Projected balance at retirement"
          trend="up"
        />
        <ResultCard
          title="Your Contributions"
          value={yourContributions}
          description="Total personal contributions"
          trend="up"
        />
        <ResultCard
          title="Employer Match"
          value={employerTotal}
          description="Total employer contributions"
          trend="up"
        />
      </div>

      <Chart data={calculatedData} />
    </div>
  );
};

export default Four01kCalculator;